import React, { useCallback, useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import useApi from "@/hooks/useApi";
import useSelectedTeam from "@/hooks/useSelectedTeam";
import LoadingSpinner from "@/components/LoadingSpinner";
import IntegrationActivityLog from "../IntegrationActivityLog";
import StarterWorkflowWizard from "../StarterWorkflowWizard";
import IntegrationNotEnabled from "../shared/IntegrationNotEnabled";
import JobberDataMapping from "./JobberDataMapping";
import LaunchWizardButton from "@/components/LaunchWizardButton";

const JOBBER_COLOR = "#4E7DDB";

// ─── Exported nav helpers ──────────────────────────────────────────────────────

export function BackToIntegrationLandingJobber() {
  const navigate = useNavigate();
  return (
    <button type="button" className="flex cursor-pointer items-center gap-2 text-slate-600"
      onClick={() => navigate("/integrations")}>
      <svg viewBox="0 0 20 20" width="16" height="16" fill="currentColor">
        <path fillRule="evenodd" d="M9.707 16.707a1 1 0 01-1.414 0l-6-6a1 1 0 010-1.414l6-6a1 1 0 011.414 1.414L5.414 9H17a1 1 0 110 2H5.414l4.293 4.293a1 1 0 010 1.414z" clipRule="evenodd" />
      </svg>
      Back to Integrations
    </button>
  );
}

// ─── Types ────────────────────────────────────────────────────────────────────

interface JobberConfig {
  id: number;
  siteId: number;
  displayName: string;
  accountName: string;
  enabled: boolean;
  createdAt: string;
  authMethod?: string;
  oauthConnectedAt?: string;
}

type TabId = "overview" | "accounts" | "ai-tools" | "data-mapping" | "logs";

// ─── Capabilities ─────────────────────────────────────────────────────────────

const CAPABILITIES = [
  {
    title: "Job status tracking",
    description: "Clients check their service job status, schedule, and assigned technician in real time"
  },
  {
    title: "Quote & estimate lookup",
    description: "Clients see their quote details, line items, and pricing without calling"
  },
  {
    title: "Invoice & payment status",
    description: "Outstanding balances, due dates, and payment history on demand"
  },
  {
    title: "Appointment scheduling",
    description: "Upcoming service appointments with time windows and technician info"
  },
  {
    title: "Service request intake",
    description: "Capture new service requests from clients 24/7 — straight into Jobber"
  }
];

// ─── AI Tools ─────────────────────────────────────────────────────────────────

const AI_TOOLS = [
  { name: "jobber_get_client", label: "Get client", group: "Clients", description: "Client profile — name, address, contact info." },
  { name: "jobber_search_clients", label: "Search clients", group: "Clients", description: "Find a client by name, email, or phone number." },
  { name: "jobber_get_job", label: "Get job", group: "Jobs", description: "Job details by job number — status, description, scheduled time, assigned tech." },
  { name: "jobber_search_jobs", label: "Search jobs", group: "Jobs", description: "Find jobs for a specific client." },
  { name: "jobber_get_upcoming_appointments", label: "Get upcoming appointments", group: "Jobs", description: "Next scheduled service appointments — dates, times, and assigned technicians." },
  { name: "jobber_get_active_work_orders", label: "Get active work orders", group: "Jobs", description: "All currently active and in-progress jobs." },
  { name: "jobber_get_quote", label: "Get quote", group: "Quotes & Invoices", description: "Quote details — line items, total, and status." },
  { name: "jobber_get_invoice", label: "Get invoice", group: "Quotes & Invoices", description: "Invoice details — items, total, balance due, and due date." },
  { name: "jobber_get_outstanding_invoices", label: "Get outstanding invoices", group: "Quotes & Invoices", description: "All unpaid invoices for a client." },
  { name: "jobber_create_request", label: "Create service request", group: "Service Requests", description: "Capture a new service request from a client — creates it directly in Jobber." },
  { name: "jobber_get_job_notes", label: "Get job notes", group: "Jobs", description: "All notes and internal comments attached to a job." },
  { name: "jobber_get_payment_status", label: "Get payment status", group: "Quotes & Invoices", description: "Payment status for an invoice — paid, partial, or outstanding." },
  { name: "jobber_get_team_members", label: "Get team members", group: "Jobs", description: "Assigned team members for a job or all active staff." },
  { name: "jobber_get_request_status", label: "Get request status", group: "Service Requests", description: "Status of a service request — pending, converted, or dismissed." },
  { name: "jobber_get_client_history", label: "Get client history", group: "Clients", description: "Complete job and visit history for a client." },
  { name: "jobber_get_service_types", label: "Get service types", group: "Jobs", description: "Available service types and categories offered by the business." }
];

// ─── Shared UI Components ─────────────────────────────────────────────────────

function CapabilitiesPanel() {
  const [open, setOpen] = useState(false);
  return (
    <div className="bg-white rounded-2xl shadow-md mb-4">
      <button type="button" onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between px-6 py-4 text-left hover:bg-slate-50 rounded-2xl" aria-expanded={open}>
        <div className="flex items-center gap-3">
          <span className="font-semibold text-slate-800">What's included</span>
          <span className="text-xs bg-green-100 text-green-700 font-medium px-2 py-0.5 rounded-full">{CAPABILITIES.length} feature areas</span>
        </div>
        <svg className={`w-4 h-4 text-slate-400 transition-transform flex-shrink-0 ${open ? "rotate-180" : ""}`} viewBox="0 0 16 16" fill="currentColor">
          <path d="M8 10.5L3 5.5H13L8 10.5Z" />
        </svg>
      </button>
      {open && (
        <div className="border-t border-slate-100 px-6 pb-6">
          <div className="mt-4 rounded-lg bg-green-50 border border-green-200 p-3 mb-4 text-xs text-green-800">
            <strong>AI tools work without any configuration.</strong> Connect Jobber (API token) and the AI gains access to jobs, quotes, invoices, and client data in real time.
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {CAPABILITIES.map((cap, i) => (
              <div key={i} className="rounded-lg border border-slate-100 bg-slate-50 p-4">
                <div className="text-sm font-medium text-slate-700 mb-0.5">{cap.title}</div>
                <div className="text-xs text-slate-500">{cap.description}</div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function HowItWorksCard() {
  return (
    <div className="bg-white rounded-2xl shadow-md p-6 mb-4">
      <div className="font-semibold text-slate-800 text-base mb-1">How Jobber works in Velaro</div>
      <p className="text-xs text-slate-500 mb-4">Connect once — every job, quote, and invoice becomes AI-searchable instantly.</p>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <div className="rounded-xl border border-slate-200 bg-slate-50 p-4 relative">
          <div className="flex items-center gap-2 mb-2">
            <span className="w-6 h-6 rounded-full text-white text-xs font-bold flex items-center justify-center flex-shrink-0" style={{ backgroundColor: JOBBER_COLOR }}>1</span>
            <span className="font-semibold text-slate-700 text-sm">Connect Jobber</span>
          </div>
          <p className="text-xs text-slate-500 mb-3">In Jobber go to Settings → Connected Apps → Generate API Token, copy the token, and paste it here.</p>
          <div className="flex items-center gap-2 text-xs text-slate-600">
            <span className="font-mono bg-white border border-slate-200 px-1.5 py-0.5 rounded text-[10px]">API Token</span>
          </div>
          <div className="hidden sm:flex absolute -right-3.5 top-1/2 -translate-y-1/2 z-10 w-7 h-7 rounded-full bg-white border border-slate-200 shadow items-center justify-center">
            <svg viewBox="0 0 10 10" width="10" height="10" fill="none"><path d="M3 2L7 5L3 8" stroke="#94a3b8" strokeWidth="1.5" strokeLinecap="round" /></svg>
          </div>
        </div>
        <div className="rounded-xl border border-green-200 bg-green-50 p-4 relative">
          <div className="flex items-center gap-2 mb-2">
            <span className="w-6 h-6 rounded-full bg-green-600 text-white text-xs font-bold flex items-center justify-center flex-shrink-0">2</span>
            <span className="font-semibold text-green-800 text-sm">10 AI tools activate</span>
          </div>
          <p className="text-xs text-green-700 mb-3">Every job, client, and invoice is instantly searchable — no extra setup.</p>
          <div className="grid grid-cols-2 gap-1">
            {["Job tracking", "Client lookup", "Quote details", "Invoice status", "Appointments", "Work orders", "Service intake", "Outstanding bills"].map((t) => (
              <div key={t} className="flex items-center gap-1 text-[10px] text-green-700">
                <svg viewBox="0 0 8 8" width="8" height="8" fill="none" className="flex-shrink-0"><path d="M1 4L3 6L7 2" stroke="#16a34a" strokeWidth="1.5" strokeLinecap="round" /></svg>
                {t}
              </div>
            ))}
          </div>
          <div className="hidden sm:flex absolute -right-3.5 top-1/2 -translate-y-1/2 z-10 w-7 h-7 rounded-full bg-white border border-slate-200 shadow items-center justify-center">
            <svg viewBox="0 0 10 10" width="10" height="10" fill="none"><path d="M3 2L7 5L3 8" stroke="#94a3b8" strokeWidth="1.5" strokeLinecap="round" /></svg>
          </div>
        </div>
        <div className="rounded-xl border border-blue-200 bg-blue-50 p-4">
          <div className="flex items-center gap-2 mb-2">
            <span className="w-6 h-6 rounded-full bg-blue-600 text-white text-xs font-bold flex items-center justify-center flex-shrink-0">3</span>
            <span className="font-semibold text-blue-800 text-sm">Scale 24/7 client support</span>
          </div>
          <p className="text-xs text-blue-700 mb-3">Answer job status, invoice, and scheduling questions instantly — without tying up your office staff.</p>
          <div className="space-y-2">
            {[
              ["Job status bot", "Clients check job status and technician ETA without calling"],
              ["Invoice assistant", "Outstanding balances and payment history on demand"],
              ["Service intake", "Capture new service requests 24/7 straight into Jobber"]
            ].map(([title, desc]) => (
              <div key={title} className="bg-white rounded-lg p-2 border border-blue-100">
                <div className="text-[10px] font-semibold text-blue-800 uppercase tracking-wide">{title}</div>
                <div className="text-[10px] text-blue-600 mt-0.5">{desc}</div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

function OverviewTab({ hasConnection, onConnect, onOpenWizard }: { hasConnection: boolean; onConnect(): void; onOpenWizard(): void }) {
  return (
    <div>
      <HowItWorksCard />
      {!hasConnection ? (
        <div className="bg-white rounded-2xl shadow-md p-6 mb-4 flex items-center justify-between gap-4">
          <div>
            <div className="font-semibold text-slate-800 mb-1">Connect your Jobber account</div>
            <p className="text-sm text-slate-500">Enter your API token and your jobs, quotes, invoices, and client data become instantly searchable by the AI during any conversation.</p>
          </div>
          <button type="button" onClick={onConnect} className="shrink-0 px-4 py-2 rounded-lg text-sm font-medium text-white" style={{ backgroundColor: JOBBER_COLOR }}>
            Connect Now →
          </button>
        </div>
      ) : (
        <div className="bg-white rounded-2xl shadow-md p-6 mb-4 flex items-center justify-between gap-4">
          <div>
            <div className="font-semibold text-slate-800 mb-1">Launch Starter Workflow Wizard</div>
            <p className="text-sm text-slate-500">Build a Job Status Bot, Invoice Assistant, or Service Request Bot in minutes using a pre-built Jobber template.</p>
          </div>
          <button type="button" onClick={onOpenWizard} className="shrink-0 px-4 py-2 rounded-lg text-sm font-medium text-white" style={{ backgroundColor: JOBBER_COLOR }}>
            Launch Wizard →
          </button>
        </div>
      )}
    </div>
  );
}

function AiToolsTab({ hasConnection, onOpenWizard }: { hasConnection: boolean; onOpenWizard(): void }) {
  const groups = [...new Set(AI_TOOLS.map((t) => t.group))];
  return (
    <div className="bg-white rounded-2xl shadow-md p-6">
      <div className="flex items-center justify-between mb-4">
        <div>
          <div className="font-semibold text-slate-800 text-lg flex items-center gap-2">
            AI Tools
            <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${hasConnection ? "bg-green-100 text-green-700" : "bg-slate-100 text-slate-500"}`}>
              {hasConnection ? `${AI_TOOLS.length} active` : "No connection"}
            </span>
          </div>
          <p className="text-sm text-slate-500 mt-1">All tools activate automatically on connect — no configuration required.</p>
        </div>
        {hasConnection && (
          <LaunchWizardButton onClick={onOpenWizard} />
        )}
      </div>
      <div className="space-y-6">
        {groups.map((group) => (
          <div key={group}>
            <div className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">{group}</div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
              {AI_TOOLS.filter((t) => t.group === group).map((tool) => (
                <div key={tool.name} className="rounded-lg border border-slate-100 bg-slate-50 p-3">
                  <div className="flex items-start gap-2">
                    <div className="w-2 h-2 rounded-full mt-1.5 flex-shrink-0" style={{ backgroundColor: JOBBER_COLOR }} />
                    <div>
                      <div className="text-xs font-semibold text-slate-700">{tool.label}</div>
                      <div className="text-[10px] font-mono text-slate-400 mb-1">{tool.name}</div>
                      <div className="text-xs text-slate-500">{tool.description}</div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function AccountsTab({
  configs,
  onRefresh,
  onAdd
}: {
  configs: JobberConfig[];
  onRefresh(): void;
  onAdd(): void;
}) {
  const api = useApi();
  const [pendingDelete, setPendingDelete] = useState<number | null>(null);
  const [deleting, setDeleting] = useState<number | null>(null);

  async function handleDelete(id: number) {
    if (pendingDelete !== id) {
      setPendingDelete(id);
      return;
    }
    setDeleting(id);
    setPendingDelete(null);
    try {
      await api.admin.delete(`Jobber/Configs/${id}`);
      onRefresh();
    } catch {
      // graceful
    } finally {
      setDeleting(null);
    }
  }

  return (
    <div className="bg-white rounded-2xl shadow-md p-6">
      <div className="flex items-center justify-between mb-4">
        <div className="font-semibold text-slate-800 text-lg">Connected Accounts</div>
        <button type="button" onClick={onAdd} className="px-3 py-1.5 rounded-lg text-xs font-medium text-white" style={{ backgroundColor: JOBBER_COLOR }}>
          + Add Account
        </button>
      </div>
      {configs.length === 0 ? (
        <div className="text-center py-12 text-slate-400">
          <div className="font-medium text-slate-600 mb-1">No Jobber account connected</div>
          <p className="text-sm">Connect your Jobber account to enable AI-powered field service support for your clients.</p>
          <button type="button" onClick={onAdd} className="mt-4 px-4 py-2 rounded-lg text-sm font-medium text-white" style={{ backgroundColor: JOBBER_COLOR }}>
            Connect Jobber →
          </button>
        </div>
      ) : (
        <div className="space-y-3">
          {configs.map((c) => (
            <div key={c.id} className="rounded-xl border border-slate-200 p-4 flex items-center justify-between gap-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg flex items-center justify-center text-white text-lg font-bold" style={{ backgroundColor: JOBBER_COLOR }}>J</div>
                <div>
                  <div className="font-medium text-slate-800">{c.displayName}</div>
                  {c.accountName && <div className="text-xs text-slate-500">{c.accountName}</div>}
                  <div className="flex items-center gap-2 mt-0.5">
                    {c.authMethod === "oauth" ? (
                      <>
                        <span className="text-xs bg-green-100 text-green-700 font-medium px-2 py-0.5 rounded-full">OAuth</span>
                        {c.oauthConnectedAt && (
                          <span className="text-xs text-slate-400">Connected {new Date(c.oauthConnectedAt).toLocaleDateString()}</span>
                        )}
                      </>
                    ) : (
                      <span className="text-xs bg-slate-100 text-slate-500 px-2 py-0.5 rounded-full">API Token</span>
                    )}
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${c.enabled ? "bg-green-100 text-green-700" : "bg-slate-100 text-slate-500"}`}>
                  {c.enabled ? "Active" : "Disabled"}
                </span>
                <button
                  type="button"
                  onClick={() => handleDelete(c.id)}
                  disabled={deleting === c.id}
                  className={`text-xs transition-colors ${pendingDelete === c.id ? "text-red-700 font-semibold" : "text-red-500 hover:text-red-700"}`}
                >
                  {deleting === c.id ? "Removing..." : pendingDelete === c.id ? "Confirm remove?" : "Remove"}
                </button>
                {pendingDelete === c.id && (
                  <button type="button" onClick={() => setPendingDelete(null)} className="text-xs text-slate-400 hover:text-slate-600 transition-colors">
                    Cancel
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function CreateForm({ onSaved, onCancel }: { onSaved(): void; onCancel(): void }) {
  const api = useApi();

  // OAuth flow state
  const [connecting, setConnecting] = useState(false);
  const [oauthError, setOauthError] = useState<string | null>(null);

  // Legacy token flow state
  const [showLegacy, setShowLegacy] = useState(false);
  const [displayName, setDisplayName] = useState("");
  const [accessToken, setAccessToken] = useState("");
  const [testing, setTesting] = useState(false);
  const [saving, setSaving] = useState(false);
  const [testResult, setTestResult] = useState<{ valid: boolean; accountName?: string; error?: string } | null>(null);

  async function handleOAuthConnect() {
    setConnecting(true);
    setOauthError(null);
    try {
      const resp = await api.admin.get("Jobber/OAuth/AuthUrl");
      if (!resp.ok) {
        const body = await resp.json().catch(() => ({})) as { error?: string };
        setOauthError(body?.error ?? "Failed to start OAuth flow. Please try again.");
        return;
      }
      const data = await resp.json() as { authUrl: string };
      window.location.href = data.authUrl;
    } catch {
      setOauthError("Failed to start OAuth flow. Please try again.");
    } finally {
      setConnecting(false);
    }
  }

  async function handleTest() {
    if (!accessToken.trim()) return;
    setTesting(true);
    setTestResult(null);
    try {
      const resp = await api.admin.post("Jobber/Test", { accessToken: accessToken.trim() });
      const data = await resp.json();
      setTestResult(data);
      if (data?.valid && data?.accountName && !displayName.trim()) {
        setDisplayName(data.accountName);
      }
    } catch {
      setTestResult({ valid: false, error: "Connection test failed." });
    } finally {
      setTesting(false);
    }
  }

  async function handleSave() {
    if (!accessToken.trim()) return;
    setSaving(true);
    try {
      const resp = await api.admin.post("Jobber/Configs", {
        displayName: displayName.trim() || "Jobber",
        accessToken: accessToken.trim()
      });
      if (resp.ok) {
        onSaved();
      }
    } catch {
      // graceful
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="mb-4 space-y-3">
      {/* Primary: OAuth */}
      <div className="bg-white rounded-2xl shadow-md p-6">
        <div className="flex items-center gap-3 mb-3">
          <div className="w-10 h-10 rounded-lg flex items-center justify-center text-white text-lg font-bold flex-shrink-0" style={{ backgroundColor: JOBBER_COLOR }}>J</div>
          <div>
            <div className="font-semibold text-slate-800">Connect with Jobber</div>
            <p className="text-xs text-slate-500">Recommended — secure OAuth 2.0, tokens refresh automatically</p>
          </div>
        </div>
        <p className="text-sm text-slate-500 mb-5">
          You'll be redirected to Jobber to authorize Velaro, then brought back automatically.
          No tokens to copy or manage.
        </p>
        {oauthError && <p className="text-red-600 text-sm mb-4">{oauthError}</p>}
        <div className="flex items-center justify-end gap-3">
          <button type="button" onClick={onCancel} className="px-4 py-2 rounded-lg text-sm font-medium border border-slate-300 text-slate-600 hover:bg-slate-50 transition-colors">
            Cancel
          </button>
          {connecting ? (
            <div className="flex items-center gap-2 text-sm text-slate-500">
              <LoadingSpinner /> Redirecting to Jobber…
            </div>
          ) : (
            <button
              type="button"
              onClick={handleOAuthConnect}
              className="px-4 py-2 rounded-lg text-sm font-medium text-white transition-colors"
              style={{ backgroundColor: JOBBER_COLOR }}
            >
              Connect with Jobber →
            </button>
          )}
        </div>
      </div>

      {/* Legacy: API Token */}
      <div className="rounded-2xl border border-slate-200 bg-slate-50 overflow-hidden">
        <button
          type="button"
          className="w-full flex items-center justify-between px-5 py-3 text-sm text-slate-400 hover:text-slate-500 transition"
          onClick={() => setShowLegacy((v) => !v)}
        >
          <span className="flex items-center gap-2">
            <span className="text-xs font-medium bg-slate-200 text-slate-500 px-2 py-0.5 rounded">Legacy</span>
            API Token
          </span>
          <svg className={`w-4 h-4 transition-transform ${showLegacy ? "rotate-180" : ""}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </button>

        {showLegacy && (
          <div className="px-5 pb-5 border-t border-slate-200">
            <p className="text-xs text-slate-400 mt-3 mb-4">
              API tokens work but are not required for Jobber Marketplace listing. Use OAuth above unless you have a specific reason to use a token.
            </p>
            <div className="space-y-3 max-w-lg">
              <div>
                <label className="block text-xs font-medium text-slate-500 mb-1">Display Name</label>
                <input
                  type="text"
                  value={displayName}
                  onChange={(e) => setDisplayName(e.target.value)}
                  placeholder="My Jobber Account"
                  className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-slate-500 mb-1">Access Token</label>
                <input
                  type="password"
                  value={accessToken}
                  onChange={(e) => setAccessToken(e.target.value)}
                  placeholder="Paste your Jobber API token"
                  className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm font-mono focus:outline-none focus:ring-2"
                />
                <p className="text-xs text-slate-400 mt-1">
                  Get your token from Jobber → Settings → Connected Apps → Generate API Token
                </p>
              </div>

              {testResult && (
                <div className={`rounded-lg px-4 py-3 text-sm ${testResult.valid ? "bg-green-50 border border-green-200 text-green-800" : "bg-red-50 border border-red-200 text-red-700"}`}>
                  {testResult.valid
                    ? `Connected successfully${testResult.accountName ? ` — ${testResult.accountName}` : ""}`
                    : testResult.error ?? "Connection failed."}
                </div>
              )}

              <div className="flex items-center gap-3 pt-1">
                <button
                  type="button"
                  onClick={handleTest}
                  disabled={testing || !accessToken.trim()}
                  className="px-4 py-2 rounded-lg text-sm font-medium border border-slate-300 text-slate-700 hover:bg-slate-100 disabled:opacity-50 transition-colors"
                >
                  {testing ? "Testing..." : "Test Connection"}
                </button>
                <button
                  type="button"
                  onClick={handleSave}
                  disabled={saving || !accessToken.trim()}
                  className="px-4 py-2 rounded-lg text-sm font-medium text-slate-600 border border-slate-300 hover:bg-slate-100 disabled:opacity-50 transition-colors"
                >
                  {saving ? "Saving..." : "Save Token"}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Main Component ────────────────────────────────────────────────────────────

export default function Jobber() {
  const api = useApi();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [configs, setConfigs] = useState<JobberConfig[]>([]);
  const [loading, setLoading] = useState(true);
  const [enabled, setEnabled] = useState(true);
  const [activeTab, setActiveTab] = useState<TabId>("overview");
  const [showWizard, setShowWizard] = useState(false);
  const [showCreate, setShowCreate] = useState(false);
  const [selectedConfigId, setSelectedConfigId] = useState<number>(0);
  const [oauthBanner, setOauthBanner] = useState<{ type: "success" | "error"; message: string } | null>(null);
  const { teamId } = useSelectedTeam();

  const fetchConfigs = useCallback(async () => {
    try {
      const resp = await api.admin.get("Jobber/Configs");
      if (resp.status === 402) { setEnabled(false); return; }
      const data = await resp.json();
      setConfigs(Array.isArray(data) ? data : []);
    } catch {
      setConfigs([]);
    } finally {
      setLoading(false);
    }
  }, [api.admin]);

  useEffect(() => { fetchConfigs(); }, [fetchConfigs]);

  // Handle OAuth redirect query params (?connected=true or ?error=...)
  useEffect(() => {
    const connected = searchParams.get("connected");
    const error = searchParams.get("error");
    if (connected === "true") {
      setOauthBanner({ type: "success", message: "Jobber connected successfully via OAuth." });
      setActiveTab("accounts");
      navigate("/integrations/jobber", { replace: true });
    } else if (error) {
      const messages: Record<string, string> = {
        invalid_state: "OAuth session expired. Please try again.",
        token_exchange_failed: "Authorization failed. Please try connecting again.",
        missing_params: "OAuth callback was missing required parameters.",
        server_error: "A server error occurred during OAuth. Please try again.",
      };
      setOauthBanner({ type: "error", message: messages[error] ?? `OAuth error: ${error}` });
      setActiveTab("accounts");
      navigate("/integrations/jobber", { replace: true });
    }
  }, [searchParams, navigate]);

  useEffect(() => {
    if (configs.length > 0 && selectedConfigId === 0) {
      setSelectedConfigId(configs.find((c) => c.enabled)?.id ?? configs[0].id);
    }
  }, [configs, selectedConfigId]);

  if (!enabled) {
    return <IntegrationNotEnabled name="Jobber Integration" description="Jobber integration requires an upgrade. Contact Velaro support to enable it for your account." />;
  }

  const hasActiveConfig = configs.some((c) => c.enabled);

  const TABS: { id: TabId; label: string }[] = [
    { id: "overview", label: "Overview" },
    { id: "accounts", label: "Accounts" },
    { id: "ai-tools", label: "AI Tools" },
    { id: "data-mapping", label: "Data Mapping" },
    { id: "logs", label: "Activity Log" }
  ];

  return (
    <div>
      {showWizard && (
        <StarterWorkflowWizard integration="jobber" onClose={() => setShowWizard(false)} />
      )}
      <CapabilitiesPanel />

      {oauthBanner && (
        <div className={`rounded-xl px-4 py-3 mb-4 flex items-center justify-between gap-3 text-sm ${oauthBanner.type === "success" ? "bg-green-50 border border-green-200 text-green-800" : "bg-red-50 border border-red-200 text-red-700"}`}>
          <span>{oauthBanner.message}</span>
          <button type="button" onClick={() => setOauthBanner(null)} className="text-xs opacity-60 hover:opacity-100 transition-opacity">✕</button>
        </div>
      )}

      <div className="flex border-b border-slate-200 mb-4">
        {TABS.map((tab) => (
          <button key={tab.id} type="button"
            className={`py-2 mr-4 text-sm cursor-pointer ${activeTab === tab.id ? "border-b-2 border-primary text-primary font-medium" : "text-slate-500"}`}
            style={activeTab === tab.id ? { borderColor: JOBBER_COLOR, color: JOBBER_COLOR } : {}}
            onClick={() => setActiveTab(tab.id)}>
            {tab.label}
            {tab.id === "accounts" && configs.length > 0 && (
              <span className="ml-1.5 text-xs bg-slate-100 text-slate-500 px-1.5 py-0.5 rounded-full">{configs.length}</span>
            )}
          </button>
        ))}
      </div>

      {hasActiveConfig && (
        <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 mb-4 flex flex-col gap-3">
          <div className="flex items-center justify-between gap-4">
            <div>
              <div className="text-sm font-medium text-blue-800 mb-0.5">
                Deploy the chat widget to your site
              </div>
              <div className="text-xs text-blue-600">
                Your Jobber integration is connected — grab your embed code from Deployments to go live.
              </div>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              <a href="/chat_window" className="text-xs px-3 py-1.5 rounded-lg bg-white border border-blue-300 text-blue-700 hover:bg-blue-100 transition">Chat Window</a>
              <a href="/deployments" className="text-xs px-3 py-1.5 rounded-lg bg-blue-600 hover:bg-blue-700 text-white transition">Get Embed Code →</a>
            </div>
          </div>
          <div className="flex items-start gap-2 bg-white/60 rounded-lg px-3 py-2 text-xs text-blue-700 border border-blue-100">
            <i className="fa-solid fa-lightbulb mt-0.5 shrink-0" />
            <span>
              <strong>Tip:</strong> Use <strong>Page Widgets</strong> from{" "}
              <a href="/deployments" className="underline hover:text-blue-900">Deployments → Page Widgets</a> to add a
              lightweight job status widget on your client portal — no live agent required.
            </span>
          </div>
        </div>
      )}

      {loading ? (
        <div className="flex justify-center py-12"><LoadingSpinner /></div>
      ) : (
        <>
          {activeTab === "overview" && (
            <OverviewTab
              hasConnection={hasActiveConfig}
              onConnect={() => { setActiveTab("accounts"); setShowCreate(true); }}
              onOpenWizard={() => setShowWizard(true)}
            />
          )}
          {activeTab === "accounts" && (
            <>
              {showCreate && (
                <CreateForm
                  onSaved={() => { setShowCreate(false); fetchConfigs(); navigate("/integrations/jobber"); }}
                  onCancel={() => setShowCreate(false)}
                />
              )}
              <AccountsTab
                configs={configs}
                onRefresh={fetchConfigs}
                onAdd={() => setShowCreate(true)}
              />
            </>
          )}
          {activeTab === "ai-tools" && <AiToolsTab hasConnection={hasActiveConfig} onOpenWizard={() => setShowWizard(true)} />}
          {activeTab === "data-mapping" && (
            <div className="bg-white rounded-2xl shadow-md p-6">
              <div className="font-semibold text-slate-800 text-lg mb-1">Data Mapping — Jobber</div>
              <JobberDataMapping
                teamId={teamId ?? 0}
                selectedConfigId={selectedConfigId}
              />
            </div>
          )}
          {activeTab === "logs" && (
            <div className="bg-white rounded-2xl shadow-md p-6">
              <IntegrationActivityLog integrationSlug="Jobber" />
            </div>
          )}
        </>
      )}
    </div>
  );
}
